import { useEffect } from 'react'
import { createPortal } from 'react-dom'
import { useThemeMode } from '@/contexts/ThemeContext'
import TOSContent from './TOSContent'
import useIsMobile from '@/hooks/useIsMobile'

interface TOSModalProps {
  onClose: () => void
}

/**
 * Modal component for displaying the Terms of Service
 */
export default function TOSModal({ onClose }: TOSModalProps) {
  const { colors } = useThemeMode()
  const isMobile = useIsMobile()

  // Get font mode from localStorage to match the app's font setting
  const fontMode = typeof window !== 'undefined'
    ? (localStorage.getItem('kyogre-font-mode') as 'departure-mono' | 'open-dyslexic' | 'system' || 'departure-mono')
    : 'departure-mono'

  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose()
      }
    }

    document.addEventListener('keydown', handleEscapeKey)
    return () => {
      document.removeEventListener('keydown', handleEscapeKey)
    }
  }, [onClose])

  return createPortal(
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100vw',
        height: '100vh',
        backgroundColor: 'rgba(0, 0, 0, 0.85)',
        display: 'flex',
        alignItems: isMobile ? 'flex-start' : 'center',
        justifyContent: 'center',
        zIndex: 9999999,
        padding: isMobile ? '0' : '40px',
        paddingTop: isMobile ? 'env(safe-area-inset-top)' : '40px',
        backdropFilter: 'blur(8px)',
        fontFamily:
          fontMode === 'departure-mono'
            ? 'var(--font-departure-mono), monospace'
            : fontMode === 'open-dyslexic'
              ? 'var(--font-open-dyslexic), sans-serif'
              : '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: colors.primaryBg,
          borderRadius: isMobile ? '0' : '10px',
          border: isMobile ? 'none' : `2px solid ${colors.primaryBorder}`,
          borderTop: isMobile ? `1px solid ${colors.primaryBorder}` : undefined,
          padding: '0',
          maxWidth: isMobile ? '100%' : '800px',
          width: isMobile ? '100%' : '90%',
          height: isMobile ? '100%' : 'auto',
          maxHeight: isMobile ? '100%' : 'calc(100vh - 80px)',
          overflowY: 'auto',
          position: 'relative',
          boxShadow: isMobile ? 'none' : '0 20px 60px rgba(0, 0, 0, 0.6), 0 0 0 1px rgba(255, 255, 255, 0.05)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          style={{
            position: 'sticky',
            top: isMobile ? '8px' : '16px',
            right: isMobile ? '8px' : '16px',
            float: 'right',
            backgroundColor: colors.secondaryBg,
            border: `1px solid ${colors.primaryBorder}`,
            color: colors.primaryText,
            cursor: 'pointer',
            fontSize: isMobile ? '16px' : '18px',
            padding: isMobile ? '8px 12px' : '6px 10px',
            lineHeight: '1',
            borderRadius: '6px',
            transition: 'all 0.2s ease',
            zIndex: 10,
            marginRight: '8px',
            marginTop: '8px',
          }}
        >
          ✕
        </button>

        {/* Header */}
        <div style={{ padding: isMobile ? '16px 16px 0 16px' : '24px 24px 0 24px' }}>
          <h2
            style={{
              fontSize: isMobile ? '16px' : '22px',
              fontWeight: '700',
              marginBottom: isMobile ? '12px' : '20px',
              color: colors.primaryText,
              letterSpacing: '-0.3px',
            }}
          >
            📜 Terms of Service
          </h2>
        </div>

        {/* TOS Content */}
        <TOSContent isMobile={isMobile} />
      </div>
    </div>,
    document.body
  )
}
